import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'
import { useRef } from 'react'

import {
  useExportableChart,
  svgToPngDataUrl
} from '@aytm/app/stats/page/question-views/components/charts/lib/exportable'

import useHistogramState from '@aytm/app/stats/page/question-views/info-view/components/charts/histogram/states/useHistogramState'

// components
import { Dropdown } from 'primereact/dropdown'

import BlockWrapper from '@aytm/app/stats/page/question-views/components/BlockWrapper'
import ChartPlaceholder from '@aytm/app/stats/page/question-views/components/charts/placeholders/Chart'
import Chart from '@aytm/app/stats/page/question-views/info-view/components/charts/histogram/Chart'

// styles
import style from '@aytm/app/stats/page/question-views/info-view/components/charts/histogram/styles/Block.module.scss'
import { CHART_HEIGHT } from '@aytm/app/stats/page/question-views/components/lib/constants'

// The info-view histogram: the bin picker and the chart it redraws. The shell owns the Backbone
// model, the report-param write and the mount; which bin count is shown is resolved here and
// reported back through the api handed up on mount.
const Block = ({
  id,
  format,
  params,
  paramsRevision,
  bars,
  ticks,
  axis,
  isAnimated,
  palette,
  answer,
  onChartExport,
  onReady,
  onStateChange
}) => {
  const { period, onModelChange } = useHistogramState({ params, paramsRevision, format, bars, onReady, onStateChange })

  const isLoading = answer === 'loading'

  // Headless export: single-output. The D3 draw (vk-family drawChart) runs in the
  // child Chart's effect — synchronous and before this parent hook's rAF, so the
  // tagged svg is final when we serialize (same ordering the vk phase verified).
  const containerRef = useRef(null)

  useExportableChart({
    deps: [answer, answer !== 'loading'],
    prepareForExport: async () => {
      const svgEl = containerRef.current?.querySelector('svg[data-capture-index]')
      if (!svgEl) throw new Error('Histogram: chart SVG not found (bad data or not rendered)')
      const w = svgEl.width.baseVal.value || Number(svgEl.getAttribute('width')) || 746
      const h = svgEl.height.baseVal.value || Number(svgEl.getAttribute('height')) || CHART_HEIGHT
      const svgString =
        '<?xml version="1.0" encoding="UTF-8" standalone="no"?>' + new XMLSerializer().serializeToString(svgEl)
      return [await svgToPngDataUrl(svgString, w, h)]
    }
  })

  const periodsList = bars.map(value => {
    return { text: value, value }
  })

  const onSelectChange = event => {
    onModelChange({ [event.target.name]: String(event.value) })
  }

  return (
    <BlockWrapper>
      <div ref={containerRef} className={style.component}>
        <div className={style.dropdownPanel}>
          <span className={style.dropdownTitle}>Bins:</span>
          <Dropdown
            className={style.dropdown}
            aria-label="Bins"
            ariaLabel="Bins"
            name="period"
            value={period}
            options={periodsList}
            optionValue="value"
            optionLabel="text"
            optionDisabled="disabled"
            fixedDropdownDir="bottom"
            onChange={onSelectChange}
          />
        </div>
        <ChartPlaceholder required isLoading={isLoading} isAnimated={isAnimated} isInverse>
          <Chart
            id={id}
            period={period}
            answer={answer}
            ticks={ticks}
            axis={axis}
            palette={palette}
            captureIndex={0}
            isAnimated={isAnimated}
            onExport={onChartExport}
          />
        </ChartPlaceholder>
      </div>
    </BlockWrapper>
  )
}

Block.displayName = 'InfoViewHistogramBlock'

Block.propTypes = {
  id: PropTypes.string,
  format: PropTypes.object,
  paramsRevision: PropTypes.number,
  params: PropTypes.shape({ hg: PropTypes.object }),
  isAnimated: PropTypes.bool,
  palette: PropTypes.arrayOf(PropTypes.string),
  bars: PropTypes.arrayOf(PropTypes.string),
  ticks: PropTypes.shape({ x: PropTypes.string, y: PropTypes.string }),
  axis: PropTypes.shape({ x: PropTypes.string, y: PropTypes.string }),
  answer: answerPropType,
  onChartExport: PropTypes.func,
  onReady: PropTypes.func,
  onStateChange: PropTypes.func
}

export default Block
